import React, { useEffect, useState, useMemo } from 'react';
import { toast } from "react-toastify";
import { useDispatch, useSelector } from 'react-redux';
import { getStatusStyle } from '@utils/constants';
import { fetchSentencesByFileIdDA } from '@store/thunks/devnagriValidationAdminThunk';
import GenericTable from "@components/tables/GenericTable";
import { BASE_URL } from "@utils/constants";
import apiClient from '@/services/api/apiClient';
import { useNavigate } from 'react-router-dom';

const SentenceDetailTableDevnagriValidatorAdmin = () => {
    const devAdminId = useSelector(state => state.auth.userId);
    const token = useSelector(state => state.auth.token);
    const sourceId = useSelector(state => state.devnagriValidatorAdmin.sourceId);
    const targetId = useSelector(state => state.devnagriValidatorAdmin.targetlanguageId);
    const { selectedAssignedFileId, selectedAssignedFolderId, assignedFiles } = useSelector(state => state.devnagriValidatorAdmin);
    const [allSentences, setAllSentences] = useState([]);
    const [inputValues, setInputValues] = useState({});
    const { fileName } = useSelector((state) => state.fileMeta);
    const { targetLanguageName } = useSelector((state) => state.fileLanguage);
    const [validatedSentences, setValidatedSentences] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [clickReject, setClickReject] = useState(false);
    const [allRejectedSentences, setAllRejectedSentences] = useState([]);
    const [loadingRejected, setLoadingRejected] = useState(false);
    const [rejectedError, setRejectedError] = useState("");
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [hasNextPage, setHasNextPage] = useState(true);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Compute paginated data
    const paginatedSentences = useMemo(() => {
        const start = pageIndex * pageSize;
        const end = start + pageSize;
        console.log("paginatedSentences:", allSentences.slice(start, end));
        return allSentences.slice(start, end);
    }, [allSentences, pageIndex, pageSize]);

    const paginatedRejectedSentences = useMemo(() => {
        const start = pageIndex * pageSize;
        const end = start + pageSize;
        console.log("paginatedRejectedSentences:", allRejectedSentences.slice(start, end));
        return allRejectedSentences.slice(start, end);
    }, [allRejectedSentences, pageIndex, pageSize]);

    // Update hasNextPage for client-side pagination
    useEffect(() => {
        console.log("allSentences:", allSentences);
        console.log("allRejectedSentences:", allRejectedSentences);
        setHasNextPage(clickReject ?
            (pageIndex + 1) * pageSize < allRejectedSentences.length :
            (pageIndex + 1) * pageSize < allSentences.length
        );
    }, [allSentences, allRejectedSentences, pageIndex, pageSize, clickReject]);

    useEffect(() => {
        const fetchSentences = async () => {
            if (!selectedAssignedFileId) return;
            setLoading(true);
            try {
                const data = await dispatch(fetchSentencesByFileIdDA(
                    selectedAssignedFileId,
                    devAdminId,
                    sourceId,
                    targetId,
                    clickReject
                ));
                console.log("Fetched sentences data:", data);
                setAllSentences(data);
                const initialValues = {};
                const initialValidated = new Set();
                data.forEach(sentence => {
                    initialValues[sentence.sentenceId] = {
                        sourceText: sentence.sourceText || "",
                        translatedText: sentence.translatedText || ""
                    };
                    if (sentence.validationStatus === "VALIDATED") {
                        initialValidated.add(sentence.sentenceId);
                    }
                });
                setInputValues(initialValues);
                setValidatedSentences(initialValidated);
            } catch (error) {
                console.error("Error fetching sentences:", error);
                setAllSentences([]);
                toast.error("Failed to fetch sentences");
            } finally {
                setLoading(false);
            }
        };
        fetchSentences();
    }, [selectedAssignedFileId, dispatch, assignedFiles, selectedAssignedFolderId, clickReject]);

    useEffect(() => {
        if (clickReject) {
            const fetchRejected = async () => {
                setLoadingRejected(true);
                setRejectedError("");
                console.log("[Rejected Sentences] Fetching with:", { selectedAssignedFileId, sourceId, targetId, devAdminId });
                try {
                    if (!token) {
                        throw new Error("No authentication token found");
                    }
                    const data = await apiClient(
                        `${BASE_URL}api/devAdmin/rejectedSentences?fileId=${selectedAssignedFileId}&sourceId=${sourceId}&targetId=${targetId}&devAdminId=${devAdminId}`,
                        {
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": `Bearer ${token}`,

                                Accept: "*/*",
                            },
                            credentials: "include",
                        },
                        navigate
                    );
                    console.log("[Rejected Sentences] API response:", data);
                    if (data.status === "success") {
                        setAllRejectedSentences(data.data || []);
                        setPageIndex(0); // Reset to first page on new data fetch
                    } else {
                        setAllRejectedSentences([]);
                        setRejectedError(data.message || "Failed to fetch rejected sentences.");
                    }
                } catch (err) {
                    setAllRejectedSentences([]);
                    setRejectedError("Failed to fetch rejected sentences. Check console for details.");
                    console.error("[Rejected Sentences] Fetch error:", err);
                }
                setLoadingRejected(false);
            };
            if (selectedAssignedFileId && sourceId && targetId && devAdminId) {
                fetchRejected();
            } else {
                console.warn("[Rejected Sentences] Missing parameters:", { selectedAssignedFileId, sourceId, targetId, devAdminId });
                setRejectedError("Missing required parameters to fetch rejected sentences.");
                setLoadingRejected(false);
            }
        }
    }, [clickReject, selectedAssignedFileId, sourceId, targetId, devAdminId]);

    const showRejectSentences = () => {
        setClickReject(!clickReject);
    };

    const allSentencesColumns = [
        {
            id: "serial_number",
            header: () => "Sr. No.",
            cell: ({ row }) => (
                <div className="text-center">
                    {pageIndex * pageSize + row.index + 1}
                </div>
            ),
            cellClassName: "text-center",
            size: 80,
        },
        {
            id: "cdacSentenceText",
            header: () => "CDAC Source Text",
            accessorKey: "cdacSentenceText",
            cell: ({ row }) => {
                const currentValues = inputValues[row.original.sentenceId] || {};
                return (
                    <div className="whitespace-normal break-words font-['Semantic/Regular']">
                        {currentValues.cdacSentenceText || row.original.cdacSentenceText || "N/A"}
                    </div>
                );
            },
            cellClassName: "whitespace-normal break-words",
            size: 250,
        },
        {
            id: "devSentenceText",
            header: () => "Dev Source Text",
            accessorKey: "devSentenceText",
            cell: ({ row }) => {
                const currentValues = inputValues[row.original.sentenceId] || {};
                return (
                    <div className="whitespace-normal break-words font-['Semantic/Regular']">
                        {row.original.devSentenceText === 'No change in CDAC source segment' ? '-' : row.original.devSentenceText}
                    </div>
                );
            },
            cellClassName: "whitespace-normal break-words",
            size: 250,
        },
        {
            id: "validatedTextGs",
            header: () => `GS Validated Text (${targetLanguageName})`,
            accessorKey: "validatedTextGs",
            cell: ({ row }) => {
                const currentValues = inputValues[row.original.sentenceId] || {};
                return (
                    <div className="whitespace-normal break-words font-['Semantic/Regular']">
                        {currentValues.validatedTextGs || row.original.validatedTextGs || "-"}
                    </div>
                );
            },
            cellClassName: "whitespace-normal break-words",
            size: 250,
        },
        {
            id: "validatedTextDev",
            header: () => `Dev Validated Text (${targetLanguageName})`,
            accessorKey: "validatedTextDev",
            cell: ({ row }) => {
                const currentValues = inputValues[row.original.sentenceId] || {};
                return (
                    <div className="whitespace-normal break-words font-['Semantic/Regular']">
                        {currentValues.validatedTextDev || row.original.validatedTextDev || "-"}
                    </div>
                );
            },
            cellClassName: "whitespace-normal break-words",
            size: 250,
        },
        {
            id: "validationStatusDev",
            header: () => "Validation Status",
            accessorKey: "validationStatusDev",
            cell: ({ row }) => {
                const style = getStatusStyle(row.original.validationStatusDev);
                return (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
                        {style.label}
                    </span>
                )
            },
            cellClassName: "text-center",
            size: 120,
        },
    ];

    const rejectedSentencesColumns = [
        {
            id: "serial_number",
            header: () => "Sr. No.",
            cell: ({ row }) => (
                <div className="text-center">
                    {pageIndex * pageSize + row.index + 1}
                </div>
            ),
            cellClassName: "text-center",
            size: 80,
        },
        {
            id: "sourceText",
            header: () => "CDAC Source Text",
            accessorKey: "cdacSentenceText",
            cell: ({ row }) => (
                <div className="whitespace-normal break-words font-['Semantic/Regular']">
                    {row.original.cdacSentenceText || "N/A"}
                </div>
            ),
            cellClassName: "whitespace-normal break-words",
            size: 300,
        },
        {
            id: "devsourceText",
            header: () => "Dev Source Text",
            accessorKey: "devSentenceText",
            cell: ({ row }) => (
                <div className="whitespace-normal break-words font-['Semantic/Regular']">
                    {row.original.devSentenceText === 'No change in CDAC source segment' ? '-' : row.original.devSentenceText}
                </div>
            ),
            cellClassName: "whitespace-normal break-words",
            size: 300,
        },
        {
            id: "translatedText",
            header: () => `Validated Text (${targetLanguageName})`,
            accessorKey: "validatedTextGs",
            cell: ({ row }) => (
                <div className="whitespace-normal break-words font-['Semantic/Regular']">
                    {row.original.validatedTextGs || "-"}
                </div>
            ),
            cellClassName: "whitespace-normal break-words",
            size: 300,
        },
        {
            id: "validationStatusDev",
            header: () => "Validation Status",
            accessorKey: "validationStatusDev",
            cell: ({ row }) => {
                const style = getStatusStyle(row.original.validationStatusDev);
                return (
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
                        {style.label}
                    </span>
                )
            },
            cellClassName: "text-center",
            size: 120,
        },
    ];

    if (!selectedAssignedFileId) {
        return (
            <div className="flex min-h-[calc(100vh-4rem)] bg-gray-50/50">
                <div className="flex-1">
                    <div className="w-full p-4 sm:p-6 lg:p-8">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <div className="text-gray-500 text-sm font-['Semantic/Strong']">
                                Please select a file to view sentences.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-[calc(100vh-4rem)] bg-gray-50/50">
            <div className="flex-1">
                <div className="w-full pt-4 sm:pt-6 lg:pt-8 px-4 sm:px-6 lg:px-8">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <h2 className="text-xl font-['Semantic/Strong'] text-center font-bold mt-2">
                            File Name: <span className="text-2xl font-['Semantic/Strong'] text-center text-[#ec7026] underline font-bold">{fileName}</span>
                        </h2>
                        <div className="px-4 sm:px-6 lg:px-8 py-4">
                            <div className="flex items-center mb-4">
                                <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                                    <button
                                        className={`px-4 py-2 text-sm font-['Semantic/Strong'] ${!clickReject
                                            ? "bg-[#ec7026] text-white"
                                            : "bg-white text-[#ec7026] hover:bg-gray-50"
                                            } transition-colors duration-200`}
                                        onClick={() => setClickReject(false)}
                                    >
                                        All Sentences
                                    </button>
                                    <button
                                        className={`px-4 py-2 text-sm font-['Semantic/Strong'] ${clickReject
                                            ? "bg-[#ec7026] text-white"
                                            : "bg-white text-[#ec7026] hover:bg-gray-50"
                                            } transition-colors duration-200`}
                                        onClick={showRejectSentences}
                                    >
                                        Rejected Sentences
                                    </button>
                                </div>
                            </div>
                            {clickReject ? (
                                loadingRejected ? (
                                    <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                                        <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                                        <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                                        <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                                    </div>
                                ) : rejectedError ? (
                                    <div className="p-6">
                                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg font-['Semantic/Strong']">
                                            <p>{rejectedError}</p>
                                            <p className="mt-2 text-sm">Please try again later or contact support.</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="w-full overflow-x-auto">
                                        <GenericTable
                                            data={paginatedRejectedSentences}
                                            columns={rejectedSentencesColumns}
                                            showPagination={true}
                                            manualPagination={true}
                                            pageIndex={pageIndex}
                                            pageSize={pageSize}
                                            hasNextPage={hasNextPage}
                                            onPaginationChange={({ pageIndex, pageSize }) => {
                                                setPageIndex(pageIndex);
                                                setPageSize(pageSize);
                                            }}
                                            emptyMessage="No rejected sentences"
                                        />
                                    </div>
                                )
                            ) : loading ? (
                                <div className="p-6 text-center text-gray-500 font-['Semantic/Strong']">
                                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                                    <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                                </div>
                            ) : (
                                <div className="w-full overflow-x-auto">
                                    <GenericTable
                                        data={paginatedSentences}
                                        columns={allSentencesColumns}
                                        showPagination={true}
                                        manualPagination={true}
                                        pageIndex={pageIndex}
                                        pageSize={pageSize}
                                        hasNextPage={hasNextPage}
                                        onPaginationChange={({ pageIndex, pageSize }) => {
                                            setPageIndex(pageIndex);
                                            setPageSize(pageSize);
                                        }}
                                        emptyMessage="No sentences available"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SentenceDetailTableDevnagriValidatorAdmin;